<template>
    <cruise-booking-page-layout
        v-if="loaded && offer"
        :search-request="searchRequest"
        :offer="offer"
        :product="product"
        :product-store="productStore"
    >
        <template v-slot:tourists="{tourists, index, disabled, personSelected, isSavingWithoutDetails, formRefs}">
            <div :data-cy="`tourist-${index}`">
                <tourist-form
                    :ref="el => formRefs.push(el)"
                    v-model="tourists[index]"
                    :disabled="disabled"
                    :index="index"
                    :offer="offer"
                    :hide-index="tourists.length === 1"
                    :selected-tourists="tourists"
                    :start-date="startDate"
                    :data-cy="`tourist-${index}`"
                    :disable-required-rule="isSavingWithoutDetails"
                    @person-selected="personSelected"
                >
                    <template v-slot:label="{hideIndex, isChild, bedType}">
                        <div class="d-flex">
                            <div v-if="!hideIndex" class="text-subtitle-1 text-capitalize font-weight-bold">
                                {{ `${$t('guest', 1)} ${index + 1} ${isChild ? `(${$t('child')})` : ''}` }}
                            </div>
                            <div v-if="bedType" class="ms-4 d-flex align-center">
                                <div v-if="bedType === 'onExtraBed'" class="font-weight-bold text-grey">
                                    <v-icon color="info" start>mdi-bed-outline</v-icon>
                                    {{ $t('extra_bed') }}
                                </div>
                                <div v-if="bedType === 'onWithoutPlace'" class="font-weight-bold text-grey">
                                    <v-icon color="warning" start>mdi-bed-empty</v-icon>
                                    {{ $t('without_bed') }}
                                </div>
                            </div>
                        </div>
                    </template>
                </tourist-form>
                <tourist-passport-form
                    :ref="el => formRefs.push(el)"
                    :index="index"
                    :disabled="disabled"
                    :corporate-policy-violation="offer.corporatePolicyViolation"
                    :disable-required-rule="isSavingWithoutDetails"
                    expanded-all
                />
            </div>
        </template>
        <template v-slot:breadcrumbs>
            <expired-offers-snackbar :is-offers-expired="isOffersExpired" @refresh="refreshExpiredOffers" />
            <cruises-breadcrumbs :search-request="searchRequest" :product="product" />
        </template>
        <template v-slot:orderCommentsForm="{bookingKey, formRefs}">
            <booking-comments-form
                :ref="el => formRefs.push(el)"
                :disabled="!!bookingKey"
                :product-store="productStore"
            />
        </template>
        <template v-slot:sidebar="{bookingKey, selectedAddOns, extraServices, prepareBookResponse}">
            <cruises-search-summary search-disabled :search-request="searchRequest" :dates="dates" />
            <cruise-order-summary
                :offer="offer"
                :info="basketItem.info"
                :prebooked="!!bookingKey"
                :extra-services="extraServices"
                :search-request="searchRequest"
                :additional-options="productStore.bookingAdditionalOptions"
                :selected-add-ons="selectedAddOns"
                :prepare-book-response="prepareBookResponse"
            />
            <cruise-offer-info-modal />
        </template>
        <conditions-modal>
            <template v-slot:info>
                <cruise-conditions-modal-info />
                <transfer-conditions-modal-info />
                <activity-conditions-modal-info />
                <hotel-conditions-modal-info />
            </template>
        </conditions-modal>
        <hotel-meal-type-info-modal />
        <hotel-room-info-modal />
        <additional-fees-modal />
        <transfer-info-modal />
        <activity-info-modal />
    </cruise-booking-page-layout>
</template>

<script lang="ts">
    import {Component, mixins, Provide} from 'vue-facing-decorator'
    import ExpiredOffersSnackbar from '@/components/search/ExpiredOffersSnackbar.vue'
    import {UTCToDate} from '@/utils/helpers'
    import BookingCommentsForm from '~src/components/booking/forms/bookingCommentsForm.src'
    import CruiseBookingPageLayout from '@/components/cruises/booking/CruiseBookingPageLayout.vue'
    import CruisesBreadcrumbs from '@/components/cruises/CruisesBreadcrumbs.vue'
    import CruisesSearchSummary from '@/components/cruises/search/CruisesSearchSummary.vue'
    import {CruisesStoreMixin} from '@/mixins/CruisesStoreMixin.vue'
    import {CruisesPageMixin} from '~src/components/cruises/mixins/CruisesPageMixin.vue'
    import CruiseOrderSummary from '@/components/cruises/booking/CruiseOrderSummary.vue'
    import ConditionsModal from '@/components/modals/ConditionsModal.vue'
    import CruiseConditionsModalInfo from '@/components/cruises/snippets/CruiseConditionsModalInfo.vue'
    import CruiseOfferInfoModal from '@/components/cruises/info/CruiseOfferInfoModal.vue'
    import TouristForm from '~src/components/booking/forms/touristForm.src'
    import TouristPassportForm from '~src/components/booking/forms/touristPassportForm.src'
    import TransferConditionsModalInfo from '@/components/transfers/snippets/TransferConditionsModalInfo.vue'
    import HotelConditionsModalInfo from '@/components/hotels/snippets/HotelConditionsModalInfo.vue'
    import ActivityConditionsModalInfo from '@/components/activities/snippets/ActivityConditionsModalInfo.vue'
    import AdditionalFeesModal from '@/components/modals/AdditionalFeesModal.vue'
    import TransferInfoModal from '@/components/transfers/info/TransferInfoModal.vue'
    import HotelRoomInfoModal from '@/components/modals/HotelRoomInfoModal.vue'
    import HotelMealTypeInfoModal from '@/components/hotels/modals/HotelMealTypeInfoModal.vue'
    import ActivityInfoModal from '@/components/activities/info/ActivityInfoModal.vue'

    @Component({
        components: {
            ActivityInfoModal,
            HotelMealTypeInfoModal,
            HotelRoomInfoModal,
            TransferInfoModal,
            AdditionalFeesModal,
            ActivityConditionsModalInfo,
            HotelConditionsModalInfo,
            TransferConditionsModalInfo,
            TouristPassportForm,
            TouristForm,
            CruiseOfferInfoModal,
            CruiseConditionsModalInfo,
            ConditionsModal,
            CruiseOrderSummary,
            CruisesSearchSummary,
            CruisesBreadcrumbs,
            CruiseBookingPageLayout,
            ExpiredOffersSnackbar,
            BookingCommentsForm,
        },
    })
    export class CruiseBookingPage extends mixins(CruisesStoreMixin, CruisesPageMixin) {
        loaded = false

        async mounted() {
            await this.$store.restored
            await this.loadRuntimeData()
            await runtimeStore.loadTouristBookingFields({offerKey: this.$route.query.offerKey})
            this.productStore.clearBookingAdditionalOptions()
            await this.productStore.getExtraServiceAdditionalOptions(this.$route.query.offerKey)
            this.loaded = true
        }

        isOffersExpired() {
            return (
                this.$dateFns.isAfter(new Date(), UTCToDate(this.$dateFns.parseISO(this.basketItem.expirationTime))) &&
                !runtimeStore.bookingActive &&
                !this.productStore.prepareBookResponse.bookingKey
            )
        }

        refreshExpiredOffers() {
            this.$router.push(this.productRuntimeStore.searchPageLink(this.searchRequest))
        }

        get basketItem() {
            return this.productStore.basketItem(this.$route.query.offerKey)
        }

        @Provide get searchRequest() {
            return this.basketItem.searchRequest
        }

        get product() {
            return this.basketItem.product
        }

        get dates() {
            return `${this.formattedDate(this.offer?.startDate)}`
        }

        formattedDate(date) {
            return date ? this.$filters.dateFormat(this.$dateFns.parseISO(date)) : ''
        }

        get offer() {
            return this.product.offers.find(offer => offer.offerKey === this.$route.query.offerKey)
        }

        get startDate() {
            return this.searchRequest.dateTime
        }
    }

    export default CruiseBookingPage
</script>
